import isEmpty from "lodash/isEmpty";
import { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import ConfirmationModal from "../../../parts/modals/ConfirmationModal";
import SvgIcon from "../../../parts/SvgIcon";
import { useRillContext } from "../../../rill/RillReact/share/base";
import { canViewAllOrganizationClients } from "../../../utils/organization-permissions-helpers";
import {
  findDemoAthleteEntry,
  hasNoAthletesOrOnlyDemoAthlete,
} from "../../../utils/user-helpers";
import { Athlete, MobileAthlete } from "./components/AthleteListCard";
import AthletesConfigList from "./components/client-configuration/AthletesConfigList";
import DemoAthleteInstructionsCard from "./components/DemoAthleteInstructionsCard";
import { Header, SubHeader } from "./components/Header";
import NoAthletesView from "./components/NoAthletesView";
import UnacknowledgedAthlete from "./components/UnacknowledgedAthlete";

const deleteGroup = (e, groupName) => {
  e.preventDefault();
  submit(`/coaching-portal/athletes/delete-group`, {
    method: "POST",
    body: {
      groupName,
    },
  });
};

const GroupSelector = ({ athleteGroup, athletesLength, search, query }) => {
  const { t } = useTranslation("coaching-portal");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const handleConfirm = (e) => {
    deleteGroup(e, athleteGroup.name);
    setIsModalOpen(false);
  };
  return (
    <hgroup className="group-content">
      {/* desktop */}
      <div className="force-mobile-hidden">
        <h3>{athleteGroup.name}</h3>
        <p className="p5">{athleteGroup.description}</p>
      </div>
      {/* mobile */}
      <div className="force-only-show-mobile">
        <div>
          <h3>{athleteGroup.name}</h3>
          <p className="p5">{athleteGroup.description}</p>
        </div>
        <div>
          <b>({t("athletes.membersCount", { count: athletesLength })})</b>
        </div>
      </div>
      <div>
        <div className="mobile-hidden">
          <b>({t("athletes.membersCount", { count: athletesLength })})</b>
        </div>
        <a
          href={`/coaching-portal/athletes/${search}&modal[type]=members-group`}
          role="button"
          data-button-type="secondary"
          data-icon
        >
          <SvgIcon
            src="/img/new-icons/manage.svg"
            alt={t("athletes.alt.manageIcon")}
          />
          {t("athletes.manage")}
        </a>
        <button
          data-button-type="secondary"
          data-action="delete"
          onClick={() => setIsModalOpen(true)}
          data-icon
          type="button"
        >
          <img
            src="/img/new-icons/red-trash.svg"
            alt={t("athletes.alt.deleteIcon")}
          />
          {t("athletes.deleteGroup")}
        </button>
        <ConfirmationModal
          isOpen={isModalOpen}
          title={t("athletes.deleteGroupModal.title")}
          contentText={t("athletes.deleteGroupModal.contentText", {
            groupName: athleteGroup.name,
          })}
          cancelButtonText={t("athletes.deleteGroupModal.cancel")}
          cancelButtonAction={handleCancel}
          confirmButtonText={t("athletes.deleteGroupModal.confirm")}
          confirmButtonAction={handleConfirm}
          icon="delete"
        />
      </div>
      {/* ADDED COMPONENT FOR MOBILE */}
      {/* <div className="group-delete-manage only-show-mobile">
        <div className="group-manage">
          <a
            href={`/coaching-portal/athletes/${search}&modal[type]=members-group`}
          >
            <img src="/img/svg/edit-pencil.svg" alt="manage" />
            Manage
          </a>
        </div>
        <div
          className="group-delete"
          onClick={(e) => deleteGroup(e, query.filterGroup)}
        >
          Delete group <img src="/img/svg/trash-alt.svg" alt="trash" />
        </div>
      </div> */}
      {/* ---------------------------------------------------- */}
      {/* <div
        className="group-delete mobile-hidden"
        onClick={(e) => deleteGroup(e, query.filterGroup)}
      >
        Delete group <img src="/img/svg/trash-alt.svg" alt="trash" />
      </div> */}
    </hgroup>
  );
};

const View = () => {
  const context = useRillContext();
  const { t } = useTranslation("coaching-portal");

  const [athletesState, setAthletesState] = useState({
    welcomeVideoShownCoach: false,
    activeToggle: isEmpty(context.locals.query)
      ? "all"
      : context.locals.query.filters?.["status.inactive"]
        ? context.locals.query.filters["status.inactive"]
        : "",
  });

  const [configSearch, setConfigSearch] = useState("");
  // Added this state to replace hover with click for mobile version
  const [selectedAthlete, setSelectedAthlete] = useState(null);
  const listViewMode = context.locals.query?.showConfigList
    ? "config-list"
    : "cards";
  const { referAsClients } = context.locals.activeUser;
  useEffect(() => {
    const { activeUser } = context.locals;
    // the property was moved into `welcomeVideos`. But some users still have this property
    // out of this new object. So we need to check both
    if (
      !activeUser.welcomeVideoShownCoach &&
      !activeUser.welcomeVideos?.welcomeVideoShownCoach
    ) {
      setAthletesState((state) => {
        return { ...state, welcomeVideoShownCoach: true };
      });
    }
  }, []);

  const updateToggleActive = (value) => {
    setAthletesState((state) => {
      return { ...state, activeToggle: value };
    });
  };

  const handleSelectedAthlete = (id) => {
    setSelectedAthlete(id);
  };

  const { locals } = context;
  const {
    athletes = [],
    query,
    activeUser,
    coachOrganizationInformation,
    organizationOwner,
    organizationRoleName,
    search,
    allAthletesFromOrganization,
  } = locals;
  //Get the name of the organization's role of the current user

  //The user is allow to see al the athletes of the coaches
  const isAllowToViewAllOrganizationsCoaches =
    canViewAllOrganizationClients(organizationRoleName);
  // A Team Coach never holds their own groups (no roster to group) — default to
  // the Owner's, same stand-in used for invites and Acknowledge's coach picker.
  const isTeamCoach = organizationRoleName === "Team Coach";

  const { activeToggle } = athletesState;
  let athletesGroups = activeUser.athletesGroups;
  if (coachOrganizationInformation) {
    athletesGroups = coachOrganizationInformation.athletesGroups;
  } else if (isTeamCoach) {
    athletesGroups = organizationOwner?.athletesGroups;
  }
  const athletesGroupsArray = athletesGroups || [];
  // Group edits only ever write the active user's own groups, so sorting is
  // offered only while those are the ones on screen.
  const canSortGroups =
    !query.organizationCoach ||
    String(query.organizationCoach) === String(activeUser._id);

  const organizationCoach = query.organizationCoach || undefined;

  const athleteGroup = query?.filterGroup
    ? athletesGroupsArray.find((elem) => elem.name === query.filterGroup)
    : null;

  const activeAthletes = athletes.filter((athlete) => {
    const unacknowledged =
      (athlete.isGymMember || athlete.requiresAcknowledgement) &&
      !athlete.acknowledged;
    return !athlete.invited && !athlete.status.inactive && !unacknowledged;
  });

  const filteredConfigAthletes = configSearch.trim()
    ? activeAthletes.filter((athlete) => {
        const q = configSearch.toLowerCase();
        return (
          athlete.name?.toLowerCase().includes(q) ||
          athlete.email?.toLowerCase().includes(q)
        );
      })
    : activeAthletes;

  const pendingAthletes = athletes.filter((athlete) => athlete.invited);

  const inactiveAthletes = athletes.filter((athlete) => {
    return athlete.status?.inactive;
  });

  const isAdminOrOwner =
    organizationRoleName === "Owner" || organizationRoleName === "Admin";

  // here we need to reformat the array of athletes from the organization
  // because they are being retreived from the db under different fields names
  const athletesToAcknowledge =
    isAdminOrOwner && allAthletesFromOrganization
      ? reformattedAthletes(allAthletesFromOrganization).filter((athlete) => {
          return (
            (athlete.isGymMember || athlete.requiresAcknowledgement) &&
            !athlete.acknowledged
          );
        })
      : athletes.filter((athlete) => {
          return (
            (athlete.isGymMember || athlete.requiresAcknowledgement) &&
            !athlete.acknowledged
          );
        });

  const filterMode = query.filters ? query.filters["status.inactive"] : false;

  if (
    hasNoAthletesOrOnlyDemoAthlete(activeUser) &&
    (!isAllowToViewAllOrganizationsCoaches ||
      !allAthletesFromOrganization.length)
  ) {
    return <NoAthletesView />;
  }

  return (
    <main className="athlete-list">
      <SubHeader
        activeToggle={activeToggle}
        updateToggleActive={updateToggleActive}
        referAsClients={referAsClients}
        locals={locals}
        isAllowToViewAllOrganizationsCoaches={
          isAllowToViewAllOrganizationsCoaches
        }
        listViewMode={listViewMode}
      />
      <Header
        athletesGroups={athletesGroups}
        referAsClients={referAsClients}
        isAllowToViewAllOrganizationsCoaches={
          isAllowToViewAllOrganizationsCoaches
        }
        hideOrganizationFilter={isTeamCoach}
        canSortGroups={canSortGroups}
        query={query}
      />
      {findDemoAthleteEntry(activeUser) ? (
        <DemoAthleteInstructionsCard
          activeUser={activeUser}
          dismissible
          className="demo-athlete-instructions-card--in-athlete-list"
        />
      ) : null}
      {athleteGroup ? (
        <GroupSelector
          athleteGroup={athleteGroup}
          athletesLength={athletes.length}
          search={search}
          query={query}
        />
      ) : null}

      {athletes.length <= 0 && (
        <p className="no-athlete">
          {t("athletes.noAthletesFound", {
            role: referAsClients
              ? t("athletes.roleClientsLower")
              : t("athletes.roleAthletesLower"),
            name: query.name,
          })}
        </p>
      )}
      {listViewMode === "config-list" ? (
        <AthletesConfigList
          // It seeds its state (loaded configs, has-data flags, loader
          // bookkeeping) from locals on mount; switching group / coach is an
          // in-app navigation that doesn't remount it, so key it on the scope
          // or the new scope's rows stay skeletons.
          key={`${query?.filterGroup ?? ""}|${query?.organizationCoach ?? ""}`}
          referAsClients={referAsClients}
          filterMode={filterMode}
          activeAthletes={filteredConfigAthletes}
          filterableAthletes={activeAthletes}
          clientConfigWidgetAvailability={
            context.locals.clientConfigWidgetAvailability
          }
          search={configSearch}
          onSearchChange={setConfigSearch}
        />
      ) : (
        <>
          {["all", false].includes(filterMode) &&
          athletesToAcknowledge?.length ? (
            <section>
              <h2 className="athlete-list-title">
                {t("athletes.sectionNew", {
                  role: referAsClients
                    ? t("athletes.roleClients")
                    : t("athletes.roleAthletes"),
                })}
              </h2>
              {athletesToAcknowledge.map((athlete, idx) => {
                return (
                  <UnacknowledgedAthlete
                    key={idx}
                    athlete={athlete}
                    activeUser={activeUser}
                  />
                );
              })}
            </section>
          ) : null}

          {["all", false].includes(filterMode) && activeAthletes?.length ? (
            <section>
              <h2 className="athlete-list-title">
                {t("athletes.sectionActive", {
                  role: referAsClients
                    ? t("athletes.roleClients")
                    : t("athletes.roleAthletes"),
                })}
              </h2>
              {activeAthletes.map((athlete) => {
                return (
                  <Fragment key={athlete._id}>
                    <MobileAthlete
                      athlete={athlete}
                      activeUser={context.locals.activeUser}
                      selectedAthlete={selectedAthlete}
                      handleSelectedAthlete={handleSelectedAthlete}
                      organizationCoach={organizationCoach}
                      organizationRoleName={organizationRoleName}
                    />
                    <Athlete
                      athlete={athlete}
                      activeUser={context.locals.activeUser}
                      selectedAthlete={selectedAthlete}
                      handleSelectedAthlete={handleSelectedAthlete}
                      organizationCoach={organizationCoach}
                      organizationRoleName={organizationRoleName}
                    />
                  </Fragment>
                );
              })}
            </section>
          ) : null}

          {["all", true].includes(filterMode) && inactiveAthletes?.length ? (
            <section>
              <h2>
                {t("athletes.sectionInactive", {
                  role: referAsClients
                    ? t("athletes.roleClients")
                    : t("athletes.roleAthletes"),
                })}
              </h2>
              {inactiveAthletes.map((athlete) => {
                return (
                  <Fragment key={athlete._id}>
                    <MobileAthlete
                      athlete={athlete}
                      pending
                      activeUser={context.locals.activeUser}
                      selectedAthlete={selectedAthlete}
                      handleSelectedAthlete={handleSelectedAthlete}
                      organizationCoach={organizationCoach}
                      organizationRoleName={organizationRoleName}
                    />
                    <Athlete
                      athlete={athlete}
                      pending
                      activeUser={context.locals.activeUser}
                      selectedAthlete={selectedAthlete}
                      handleSelectedAthlete={handleSelectedAthlete}
                      organizationCoach={organizationCoach}
                      organizationRoleName={organizationRoleName}
                    />
                  </Fragment>
                );
              })}
            </section>
          ) : null}
          {["all", false].includes(filterMode) && pendingAthletes?.length ? (
            <section>
              <h2>{t("athletes.sectionPendingInvites")}</h2>
              {pendingAthletes.map((athlete) => {
                return (
                  <Fragment key={athlete._id || athlete.email}>
                    <MobileAthlete
                      athlete={athlete}
                      pending
                      activeUser={context.locals.activeUser}
                      selectedAthlete={selectedAthlete}
                      handleSelectedAthlete={handleSelectedAthlete}
                      organizationCoach={organizationCoach}
                      organizationRoleName={organizationRoleName}
                    />
                    <Athlete
                      athlete={athlete}
                      pending
                      activeUser={context.locals.activeUser}
                      selectedAthlete={selectedAthlete}
                      handleSelectedAthlete={handleSelectedAthlete}
                      organizationCoach={organizationCoach}
                      organizationRoleName={organizationRoleName}
                    />
                  </Fragment>
                );
              })}
            </section>
          ) : null}
        </>
      )}
    </main>
  );
};

export default View;

const reformattedAthletes = (athletesToReformat) => {
  const athletes = athletesToReformat.map(
    ({
      athleteName,
      athleteAvatar,
      athleteIsGymMember,
      athleteAcknowledged,
      athleteEmail,
      athleteId,
      coachId,
      coachName,
      athleteRequiresAcknowledgement,
    }) => ({
      _id: athleteId,
      coach: { _id: coachId, name: coachName },
      name: athleteName,
      email: athleteEmail,
      avatar: athleteAvatar,
      isGymMember: athleteIsGymMember,
      acknowledged: athleteAcknowledged,
      requiresAcknowledgement: athleteRequiresAcknowledgement,
    })
  );
  return athletes;
};
